import { LitElement, html, nothing } from 'lit';
import './components/index';
import { TPCRouter } from './utils/router';
import { customElement, property, state } from 'lit/decorators.js';
import { QuestionListResponse } from './types';
import { AppDispatch, store } from './redux/store';
import { ReduxController } from './redux/utils/ReduxController';
import tpcQuestionApi from './redux/api/questionApiSlice';
import { appStateSlice } from './redux/appStateSlice';
import { AuthController } from '../user-area/controllers/auth-controller';
import { SubscriptionController } from '../user-area/controllers/subscription-controller';
@customElement('tm-tpc-corrector')
export class TmTPCCorrector extends LitElement {
    createRenderRoot() {
        return this;
    }
    dispatch = store.dispatch as AppDispatch;
    rxController = new ReduxController(this);
    get store() {
        return this.rxController.state;
    }

    authController = new AuthController(this);
    subscriptionController = new SubscriptionController(this);

    get user() {
        return this.authController.user;
    }

    get isPremiumUser() {
        return this.user?.isPremium || this.subscriptionController.isPremiumActive;
    }

    @property({ type: Boolean })
    isLandingPage = false;

    @property({ type: Boolean })
    isWidget = false;

    @property({ type: Object })
    initialHistoryData: QuestionListResponse | undefined;

    @property({ type: Number })
    initialUsage = 0;

    @property({ type: Boolean })
    isTutor = false;

    @state()
    isLoading = false;

    @state()
    error = false;

    @state()
    isLastPage = false;

    @state()
    isFreeLimitReached = false;

    @state()
    nextCursor: string | null = null;

    get questions() {
        return (
            (this.store.tpcQuestionApi.queries.getPastCorrections?.data as QuestionListResponse)
                ?.data?.questions || []
        );
    }

    get selectedQuestionUuid() {
        return this.store.appState.selectedQuestionUuid;
    }

    async fetchNextPage() {
        if (this.isLoading || this.isLastPage || this.isFreeLimitReached) {
            return;
        }
        this.isLoading = true;
        this.error = false;
        const response = await this.dispatch(
            tpcQuestionApi.endpoints.getPastCorrections.initiate({
                cursor: this.nextCursor,
                isTutor: this.isTutor,
            })
        );
        if (!response.isSuccess) {
            this.error = true;
        } else {
            this.isLastPage = response.data.data.is_last_page;
            this.nextCursor = response.data.data.next_cursor;
            this.isFreeLimitReached = response.data.data.is_free_limit_reached || false;
        }
        this.isLoading = false;
    }

    removeSkeleton() {
        const skeleton = document.getElementById('tpc-corrector-skeleton');
        if (skeleton) {
            skeleton.remove();
        }
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.removeSkeleton();
    }

    async fillInitialData() {
        if (this.initialHistoryData && this.initialHistoryData.data) {
            this.isLastPage = this.initialHistoryData.data.is_last_page;
            this.nextCursor = this.initialHistoryData.data.next_cursor;
            this.isFreeLimitReached = this.initialHistoryData.data.is_free_limit_reached || false;
            await this.dispatch(
                tpcQuestionApi.util.upsertQueryData(
                    'getPastCorrections',
                    { cursor: null, isTutor: this.isTutor },
                    this.initialHistoryData
                )
            );
        }
        this.fetchNextPage();
    }

    firstUpdated() {
        this.dispatch(appStateSlice.actions.setUsage(this.initialUsage));
        this.dispatch(appStateSlice.actions.setIsTutor(this.isTutor));
        if (!this.isLandingPage && !this.isWidget) {
            this.fillInitialData();
            TPCRouter.getInstance().setupRouter(
                document.querySelector('tm-tpc-corrector #outlet') as HTMLElement,
                this.isTutor
            );
        }
    }

    widgetAskNewQuestion() {
        this.dispatch(appStateSlice.actions.setSelectedQuestionUuid(null));
        requestAnimationFrame(() => {
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    render() {
        if (this.isLandingPage) {
            return html`<tpc-corrector-question-create
                .isLandingPage=${this.isLandingPage}
            ></tpc-corrector-question-create>`;
        }

        if (this.isWidget) {
            return html`
                <div class="container container--tpc container--tpc-widget">
                    <div class="tpc-corrector-container tpc-corrector-container--widget">
                        ${this.selectedQuestionUuid
                            ? html`<tpc-corrector-question-view
                                  questionUuid=${this.selectedQuestionUuid}
                                  .isWidget=${this.isWidget}
                                  @askNewQuestion=${this.widgetAskNewQuestion}
                              ></tpc-corrector-question-view>`
                            : html`<tpc-corrector-question-create
                                  .isWidget=${this.isWidget}
                              ></tpc-corrector-question-create>`}
                    </div>
                </div>
            `;
        }

        return html`<tm-tpc-corrector-navbar-mobile
                .questions=${this.questions}
                .selectedQuestionUuid=${this.selectedQuestionUuid}
                .isLoading=${this.isLoading}
                .isLastPage=${this.isLastPage}
                .error=${this.error}
                .usage=${this.store.appState.usage}
                .isPremiumUser=${this.isPremiumUser}
                .isTutor=${this.isTutor}
                @bottomScrollReached=${this.fetchNextPage}
            ></tm-tpc-corrector-navbar-mobile>
            <div class="container container--tpc">
                <div class="tpc-corrector-container">
                    ${this.isTutor
                        ? html`<div id="outlet" class="tpc-corrector-outlet"></div>`
                        : nothing}
                    <tm-tpc-corrector-navbar
                        .questions=${this.questions}
                        .selectedQuestionUuid=${this.selectedQuestionUuid}
                        .isLoading=${this.isLoading}
                        .isLastPage=${this.isLastPage}
                        .error=${this.error}
                        .usage=${this.store.appState.usage}
                        .isPremiumUser=${this.isPremiumUser}
                        @bottomScrollReached=${this.fetchNextPage}
                    ></tm-tpc-corrector-navbar>
                    ${!this.isTutor
                        ? html`<div id="outlet" class="tpc-corrector-outlet"></div>`
                        : nothing}
                </div>
            </div> `;
    }
}
